import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Button,
  TextField,
  Switch,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Paper,
  Divider,
  Chip,
  Tooltip,
  Autocomplete
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { ApiService } from '../../services/api';

interface IndustryTag {
  id: number;
  name: string;
  status: number; // 1 = Active, 0 = Inactive
  type_: number | null;
  observations: string | null;
  newsletter: string | null;
}

const IndustryTagView: React.FC = () => {
  const [tags, setTags] = useState<IndustryTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'company' | 'personal'>('all');
  const [showRules, setShowRules] = useState(false);
  
  // Track edited rows to highlight unsaved changes or show save state
  const [editedRows, setEditedRows] = useState<Record<number, Partial<IndustryTag>>>({});
  const [savingRows, setSavingRows] = useState<Record<number, boolean>>({});

  // New tag draft state
  const [newName, setNewName] = useState('');
  const [newObservations, setNewObservations] = useState('');
  const [newStatus, setNewStatus] = useState(true);
  const [newType, setNewType] = useState<number | ''>(1); // Defaults to Company tag
  const [newNewsletter, setNewNewsletter] = useState<string | ''>('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getIndustryTags(null, 1000, 'name', 'ASC', true);
      if (response && Array.isArray(response.data)) {
        setTags(response.data);
        setEditedRows({}); // clear edit drafts
      } else {
        setTags([]);
      }
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to fetch industry tags.');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (id: number, field: keyof IndustryTag, value: any) => {
    setEditedRows(prev => {
      const currentDraft = prev[id] || {};
      const originalTag = tags.find(t => t.id === id);
      if (!originalTag) return prev;

      const updatedDraft = {
        ...currentDraft,
        [field]: value
      };

      // If the field goes back to its original value, we can optionally clean it up, 
      // but keeping it simple is robust.
      return {
        ...prev,
        [id]: updatedDraft
      };
    });
  };

  const handleSaveRow = async (id: number) => {
    const draft = editedRows[id];
    if (!draft) return;

    const originalTag = tags.find(t => t.id === id);
    if (!originalTag) return;

    const payload = {
      name: draft.name !== undefined ? draft.name : originalTag.name,
      status: draft.status !== undefined ? draft.status : originalTag.status,
      type_: draft.type_ !== undefined ? draft.type_ : originalTag.type_,
      observations: draft.observations !== undefined ? draft.observations : originalTag.observations,
      newsletter: draft.newsletter !== undefined ? draft.newsletter : originalTag.newsletter
    };

    if (!payload.name.trim()) {
      setError('Tag name cannot be empty');
      return;
    }

    setSavingRows(prev => ({ ...prev, [id]: true }));
    setError(null);
    setSuccess(null);

    try {
      await ApiService.updateIndustryTag(id, {
        name: payload.name.trim(),
        status: payload.status,
        type_: payload.type_ !== null && payload.type_ !== undefined ? Number(payload.type_) : null,
        observations: payload.observations ? payload.observations.trim() : null,
        newsletter: payload.newsletter || null
      });

      // Update local state to reflect successful save
      setTags(prev => prev.map(t => t.id === id ? { ...t, ...payload } : t));
      setEditedRows(prev => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
      setSuccess(`Saved changes for tag "${payload.name}"`);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to save tag changes.');
    } finally {
      setSavingRows(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleDeleteRow = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to permanently delete the tag "${name}"?`)) {
      return;
    }

    setSavingRows(prev => ({ ...prev, [id]: true }));
    setError(null);
    setSuccess(null);

    try {
      await ApiService.deleteIndustryTag(id);
      setTags(prev => prev.filter(t => t.id !== id));
      setEditedRows(prev => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
      setSuccess(`Tag "${name}" was successfully deleted.`);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to delete tag.');
    } finally {
      setSavingRows(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleCreateTag = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) {
      setError('New tag name cannot be empty');
      return;
    }

    setCreating(true);
    setError(null);
    setSuccess(null);

    try {
      await ApiService.createIndustryTag({
        name: newName.trim(),
        status: newStatus ? 1 : 0,
        type_: newType !== '' ? Number(newType) : null,
        observations: newObservations.trim() || null,
        newsletter: newNewsletter || null
      });

      setSuccess(`Created new tag "${newName.trim()}"`);
      setNewName('');
      setNewObservations('');
      setNewStatus(true);
      setNewType(1);
      setNewNewsletter('');
      loadTags();
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to create new tag.');
    } finally {
      setCreating(false);
    }
  };

  // Filter tags based on status filter and search query
  const filteredTags = tags.filter(tag => {
    // 1. Filter by Active/Inactive status selection
    const draft = editedRows[tag.id] || {};
    const currentStatus = draft.status !== undefined ? draft.status : tag.status;
    if (statusFilter === 'active' && currentStatus !== 1) return false;
    if (statusFilter === 'inactive' && currentStatus !== 0) return false;

    // 2. Filter by Tag Type selection (Company vs Personal)
    const currentType = draft.type_ !== undefined ? draft.type_ : tag.type_;
    const isCompany = currentType === 1; // 1 is Company, anything else (0, null, undefined) is Personal
    if (typeFilter === 'company' && !isCompany) return false;
    if (typeFilter === 'personal' && isCompany) return false;

    // 3. Filter by Search Query
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;

    // Get current values (either draft edit or original)
    const name = (draft.name !== undefined ? draft.name : tag.name).toLowerCase();
    const rawObs = draft.observations !== undefined ? draft.observations : tag.observations;
    const obs = (rawObs || '').toLowerCase();

    return name.includes(q) || obs.includes(q) || String(tag.id) === q;
  });

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, p: 1 }}>
      {/* Action Banner / Messages */}
      {error && (
        <Alert severity="error" sx={{ borderRadius: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ borderRadius: 1.5 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* General Guidelines Collapsible Drawer */}
      <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', mb: 0.5 }}>
        <Box 
          onClick={() => setShowRules(!showRules)}
          sx={{ 
            p: 1.5, 
            backgroundColor: '#f8fafc', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            cursor: 'pointer',
            transition: 'background-color 0.15s',
            '&:hover': { backgroundColor: '#f1f5f9' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ color: '#2563eb' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
              GENERAL CANNABIS TAGGING MANUAL & OVERARCHING DOMAIN GUIDELINES
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: '#2563eb', fontWeight: 700, fontSize: '0.75rem' }}>
            {showRules ? 'HIDE MANUAL RULES' : 'VIEW OVERARCHING RULES'}
          </Typography>
        </Box>
        {showRules && (
          <Box sx={{ p: 2, backgroundColor: 'white' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1, borderBottom: '2px solid #2563eb', pb: 0.5, fontSize: '0.8rem' }}>
                  🌿 General Cannabis vs. Hemp Rules
                </Typography>
                <Typography variant="body2" component="div" sx={{ color: '#475569', lineHeight: 1.6, fontSize: '0.75rem' }}>
                  <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                    <li style={{ marginBottom: '6px' }}><strong>The 0.3% Rule:</strong> Hemp (CBD) has less than 0.3% THC. CBD products are legalized via the Farm Act and can be credit card processed and mailed.</li>
                    <li style={{ marginBottom: '6px' }}><strong>Cannabis (THC):</strong> Subject to heavy state-level restrictions, high taxes, and strict licensing. Cannot be legally purchased online with credit cards or mailed.</li>
                    <li style={{ marginBottom: '6px' }}><strong>Tag Preference:</strong> Always prioritize plant-related tags (direct license contact) over fluff-related tags. Avoid adding too many secondary tags.</li>
                  </ul>
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1, borderBottom: '2px solid #2563eb', pb: 0.5, fontSize: '0.8rem' }}>
                  🔍 Domain & Site Detection Tips
                </Typography>
                <Typography variant="body2" component="div" sx={{ color: '#475569', lineHeight: 1.6, fontSize: '0.75rem' }}>
                  <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                    <li style={{ marginBottom: '4px' }}><strong>.edu & .gov Domains:</strong> Always tag as <strong>NOT CANNABIS</strong>, even if they have a cannabis/hemp program or page.</li>
                    <li style={{ marginBottom: '4px' }}><strong>Credit Card Logos:</strong> If the site takes Visa/MC/Discover directly online, it is almost always a <strong>CBD Store</strong>, not THC.</li>
                    <li style={{ marginBottom: '4px' }}><strong>"Wellness" or "Hemp" in name:</strong> Highly likely to be CBD-derived (not recreational THC).</li>
                    <li style={{ marginBottom: '4px' }}><strong>Doctors / Referrals:</strong> Doctors never sell marijuana directly. They only offer recommendation letters. Medical patients pay for tax discounts.</li>
                    <li style={{ marginBottom: '4px' }}><strong>Dispensaries:</strong> Strictly tag as <strong>THC</strong> (not CBD). Selling hemp-derived CBD in dispensaries is illegal in most states.</li>
                    <li style={{ marginBottom: '4px' }}><strong>Subdomains:</strong> Do not capture subdomains. Always extract emails from the main/parent domain.</li>
                  </ul>
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Control row with instant search and refresh */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search tag names or description text..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, maxWidth: 500, backgroundColor: 'white' }}
        />
        <FormControl size="small" variant="outlined" sx={{ minWidth: 150, backgroundColor: 'white' }}>
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as 'all' | 'company' | 'personal')}
          >
            <MenuItem value="all">All Tag Types</MenuItem>
            <MenuItem value="company">Company Tags Only</MenuItem>
            <MenuItem value="personal">Personal Tags Only</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" variant="outlined" sx={{ minWidth: 150, backgroundColor: 'white' }}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="active">Active Only</MenuItem>
            <MenuItem value="inactive">Inactive Only</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadTags}
          disabled={loading}
          sx={{ textTransform: 'none', height: 40, borderRadius: 1.5 }}
        >
          Refresh List
        </Button>
      </Box>

      {/* Quick Add Tag Card - Right inline at top */}
      <Paper elevation={0} sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, backgroundColor: '#f8fafc', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon fontSize="small" sx={{ color: '#2563eb' }} /> QUICK ADD NEW TAXONOMY TAG
        </Typography>
        <form onSubmit={handleCreateTag}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={2}>
              <TextField
                label="New Tag Name"
                size="small"
                fullWidth
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Ancillary Support"
                sx={{ backgroundColor: 'white' }}
              />
            </Grid>
            <Grid item xs={12} sm={1.5}>
              <FormControl size="small" fullWidth sx={{ backgroundColor: 'white' }}>
                <Select
                  value={newType}
                  onChange={(e) => setNewType(e.target.value === '' ? '' : Number(e.target.value))}
                  displayEmpty
                >
                  <MenuItem value={1}>Company Tag</MenuItem>
                  <MenuItem value={0}>Personal Tag</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Autocomplete
                multiple
                size="small"
                options={['thc', 'cbd', 'med', 'inv']}
                value={newNewsletter ? newNewsletter.split(',').map(s => s.trim().toLowerCase()).filter(Boolean) : []}
                onChange={(event, newValue) => {
                  setNewNewsletter(newValue.join(','));
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.toUpperCase()}
                      size="small"
                      style={{
                        backgroundColor:
                          option === 'thc' ? '#eff6ff' :
                          option === 'cbd' ? '#f0fdf4' :
                          option === 'med' ? '#fdf2f8' : '#faf5ff',
                        color:
                          option === 'thc' ? '#1e40af' :
                          option === 'cbd' ? '#166534' :
                          option === 'med' ? '#9d174d' : '#6b21a8',
                        fontWeight: 600,
                        height: '22px',
                        fontSize: '0.75rem'
                      }}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    variant="outlined" 
                    placeholder="Newsletters" 
                    sx={{ backgroundColor: 'white' }}
                    InputProps={{
                      ...params.InputProps,
                      style: { fontSize: '0.8rem', padding: '1px 3px' }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="AI Guideline / Observations"
                size="small"
                fullWidth
                value={newObservations}
                onChange={(e) => setNewObservations(e.target.value)}
                placeholder="Gemini reads this text directly..."
                sx={{ backgroundColor: 'white' }}
              />
            </Grid>
            <Grid item xs={12} sm={1.2} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Active</Typography>
                <Switch
                  checked={newStatus}
                  onChange={(e) => setNewStatus(e.target.checked)}
                  color="primary"
                  size="small"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={1.3}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={creating || !newName.trim()}
                sx={{ 
                  backgroundColor: '#2563eb', 
                  '&:hover': { backgroundColor: '#1d4ed8' },
                  textTransform: 'none',
                  fontWeight: 600,
                  height: 40
                }}
              >
                {creating ? <CircularProgress size={20} color="inherit" /> : 'Create Tag'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* The Master Direct-Edit Table Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={40} thickness={4} sx={{ color: '#2563eb' }} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', pl: 1 }}>
            TAXONOMY DIRECTORY ({filteredTags.length} tags listed) — Click directly in any Name or Guideline to edit. Local changes highlight in blue.
          </Typography>

          <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', backgroundColor: 'white' }}>
            {/* Table Header Row */}
            <Box sx={{ backgroundColor: '#f8fafc', py: 1, px: 2, borderBottom: '1px solid #e2e8f0', display: { xs: 'none', sm: 'block' } }}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item sm={1}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>STATUS</Typography>
                </Grid>
                <Grid item sm={2}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>TAG NAME</Typography>
                </Grid>
                <Grid item sm={1.8}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>TAG TYPE</Typography>
                </Grid>
                <Grid item sm={2}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>DEFAULT NEWSLETTER</Typography>
                </Grid>
                <Grid item sm={4}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>AI GUIDELINE / OBSERVATION (CLICK TO EDIT)</Typography>
                </Grid>
                <Grid item sm={1.2} sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>ACTIONS</Typography>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {filteredTags.map((tag) => {
                const draft = editedRows[tag.id] || {};
                const isSaving = savingRows[tag.id] || false;
                
                // Current display values (either draft edit or original)
                const currentName = draft.name !== undefined ? draft.name : tag.name;
                const currentStatus = draft.status !== undefined ? draft.status : tag.status;
                const currentType = draft.type_ !== undefined ? draft.type_ : tag.type_;
                const currentObservations = draft.observations !== undefined ? draft.observations : (tag.observations || '');
                const currentNewsletter = draft.newsletter !== undefined ? draft.newsletter : (tag.newsletter || '');
                
                const isModified = Object.keys(draft).length > 0;

                return (
                  <Box 
                    key={tag.id}
                    sx={{
                      py: 0.5,
                      px: 2,
                      borderBottom: '1px solid #f1f5f9',
                      backgroundColor: isModified ? '#eff6ff' : currentStatus === 1 ? 'white' : '#f8fafc',
                      transition: 'background-color 0.1s',
                      '&:hover': {
                        backgroundColor: isModified ? '#eff6ff' : '#f8fafc'
                      }
                    }}
                  >
                    <Grid container spacing={1.5} alignItems="center">
                      
                      {/* ID & Status Toggle Column */}
                      <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#94a3b8', minWidth: 24 }}>
                          #{tag.id}
                        </Typography>
                        <Switch
                          size="small"
                          checked={currentStatus === 1}
                          onChange={(e) => handleFieldChange(tag.id, 'status', e.target.checked ? 1 : 0)}
                          color="primary"
                        />
                      </Grid>

                      {/* Click-to-Edit Tag Name Column */}
                      <Grid item xs={12} sm={2}>
                        <TextField
                          value={currentName}
                          onChange={(e) => handleFieldChange(tag.id, 'name', e.target.value)}
                          placeholder="Tag Name"
                          size="small"
                          fullWidth
                          variant="standard"
                          InputProps={{
                            disableUnderline: true,
                            sx: { 
                              fontWeight: 700, 
                              color: '#1e293b',
                              fontSize: '0.85rem',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              transition: 'all 0.15s',
                              border: '1px solid transparent',
                              '&:hover': {
                                borderColor: '#cbd5e1',
                                backgroundColor: '#f1f5f9'
                              },
                              '&.Mui-focused': {
                                borderColor: '#2563eb',
                                backgroundColor: 'white',
                                boxShadow: '0 0 0 1px rgba(37, 99, 235, 0.2)'
                              },
                              backgroundColor: isModified ? '#dbeafe' : 'transparent'
                            }
                          }}
                        />
                      </Grid>

                      {/* Tag Type Column (Company vs Personal) */}
                      <Grid item xs={12} sm={1.8}>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={currentType === 1 ? 1 : 0}
                            onChange={(e) => handleFieldChange(tag.id, 'type_', Number(e.target.value))}
                            sx={{
                              fontSize: '0.8rem',
                              backgroundColor: isModified ? '#dbeafe' : 'transparent',
                              fontWeight: 600,
                              color: currentType === 1 ? '#0284c7' : '#e11d48',
                              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                              '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
                              '& .MuiSelect-select': { padding: '4px 8px' }
                            }}
                          >
                            <MenuItem value={1} sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#0284c7' }}>Company Tag</MenuItem>
                            <MenuItem value={0} sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#e11d48' }}>Personal Tag</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Default Newsletter Autocomplete Column */}
                      <Grid item xs={12} sm={2}>
                        <Autocomplete
                          multiple
                          size="small"
                          options={['thc', 'cbd', 'med', 'inv']}
                          value={currentNewsletter ? currentNewsletter.split(',').map(s => s.trim().toLowerCase()).filter(Boolean) : []}
                          onChange={(event, newValue) => {
                            handleFieldChange(tag.id, 'newsletter', newValue.join(','));
                          }}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                label={option.toUpperCase()}
                                size="small"
                                style={{
                                  backgroundColor:
                                    option === 'thc' ? '#eff6ff' :
                                    option === 'cbd' ? '#f0fdf4' :
                                    option === 'med' ? '#fdf2f8' : '#faf5ff',
                                  color:
                                    option === 'thc' ? '#1e40af' :
                                    option === 'cbd' ? '#166534' :
                                    option === 'med' ? '#9d174d' : '#6b21a8',
                                  fontWeight: 600,
                                  height: '16px',
                                  fontSize: '0.6rem'
                                }}
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField 
                              {...params} 
                              variant="standard" 
                              placeholder="Select" 
                              InputProps={{
                                ...params.InputProps,
                                disableUnderline: true,
                                style: { fontSize: '0.75rem', padding: '1px 4px' }
                              }}
                            />
                          )}
                          sx={{
                            backgroundColor: isModified ? '#dbeafe' : 'transparent',
                            borderRadius: '4px',
                            border: '1px solid transparent',
                            '&:hover': { borderColor: '#cbd5e1' }
                          }}
                        />
                      </Grid>

                      {/* Click-to-Edit Observations Description Column */}
                      <Grid item xs={12} sm={4}>
                        <TextField
                          value={currentObservations}
                          onChange={(e) => handleFieldChange(tag.id, 'observations', e.target.value)}
                          placeholder="Define guidelines for Gemini here..."
                          size="small"
                          fullWidth
                          multiline
                          maxRows={3}
                          variant="standard"
                          InputProps={{
                            disableUnderline: true,
                            sx: { 
                              fontSize: '0.8rem',
                              color: '#475569',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              transition: 'all 0.15s',
                              border: '1px solid transparent',
                              '&:hover': {
                                borderColor: '#cbd5e1',
                                backgroundColor: '#f1f5f9'
                              },
                              '&.Mui-focused': {
                                borderColor: '#2563eb',
                                backgroundColor: 'white',
                                boxShadow: '0 0 0 1px rgba(37, 99, 235, 0.2)'
                              },
                              backgroundColor: isModified ? '#dbeafe' : 'transparent'
                            }
                          }}
                        />
                      </Grid>

                      {/* Row Action Buttons Column */}
                      <Grid item xs={12} sm={1.2} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
                        {isModified && (
                          <IconButton
                            color="primary"
                            size="small"
                            disabled={isSaving}
                            onClick={() => handleSaveRow(tag.id)}
                            sx={{ 
                              border: '1px solid #93c5fd', 
                              borderRadius: 1.5, 
                              color: '#2563eb',
                              backgroundColor: '#eff6ff',
                              height: 28,
                              width: 28,
                              '&:hover': { backgroundColor: '#dbeafe' }
                            }}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        )}
                        
                        <IconButton 
                          color="error" 
                          size="small"
                          disabled={isSaving}
                          onClick={() => handleDeleteRow(tag.id, currentName)}
                          sx={{ 
                            border: '1px solid #fca5a5', 
                            borderRadius: 1.5, 
                            color: '#ef4444',
                            backgroundColor: '#fef2f2',
                            height: 28,
                            width: 28,
                            '&:hover': { backgroundColor: '#fee2e2' }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>

                    </Grid>
                  </Box>
                );
              })}
            </Box>
          </Paper>

          {filteredTags.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8, border: '1px dashed #cbd5e1', borderRadius: 2, backgroundColor: '#f8fafc' }}>
              <Typography variant="body1" color="text.secondary">
                No industry tags match your search query.
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default IndustryTagView;
